import axios from 'axios'

const createDataModule = (resourceName, apiEndpoint) => ({
  namespaced: true,
  state: {
    data: [],
    currentPage: 1,
    entriesPerPage: 10,
    totalEntries: 0,
    totalPages: 1,
    search: '',
    orderBy: 'id',
    sortDirection: 'asc',
    loading: false,
    error: null
  },
  mutations: {
    setData(state, data) {
      state.data = data
    },
    setCurrentPage(state, page) {
      state.currentPage = page
    },
    setEntriesPerPage(state, entries) {
      state.entriesPerPage = entries
    },
    setTotalEntries(state, total) {
      state.totalEntries = total
    },
    setTotalPages(state, pages) {
      state.totalPages = pages
    },
    setSearch(state, search) {
      state.search = search
    },
    setOrderBy(state, orderBy) {
      state.orderBy = orderBy
    },
    setSortDirection(state, direction) {
      state.sortDirection = direction
    },
    setLoading(state, isLoading) {
      state.loading = isLoading
    },
    setError(state, error) {
      state.error = error
    }
  },
  actions: {
    async fetchData({ commit, rootState }, { tenantId, page, entriesPerPage, search, orderBy, sortDirection }) {
      commit('setLoading', true)
      commit('setError', null)
      try {
        const url = `${apiEndpoint.replace(':tenantId', tenantId)}`
        const params = {
          page: page,
          per_page: entriesPerPage,
          search: search,
          order_by: orderBy,
          sort_direction: sortDirection
        }
        const response = await axios.get(url, {
          params,
          headers: { Authorization: `Bearer ${rootState.token}` }
        })
        commit('setData', response.data.data)
        commit('setTotalEntries', response.data.totalEntries)
        commit('setTotalPages', response.data.totalPages)
        commit('setCurrentPage', page)
        commit('setEntriesPerPage', entriesPerPage)
        commit('setSearch', search)
        commit('setOrderBy', orderBy)
        commit('setSortDirection', sortDirection)
        return response.data
      } catch (error) {
        commit('setError', error.response?.data?.message || `Failed to fetch ${resourceName}`)
        throw error
      } finally {
        commit('setLoading', false)
      }
    },

    async createData({ commit, rootState }, { tenantId, data, isFormData = false }) {
      commit('setLoading', true)
      commit('setError', null)
      try {
        const url = `${apiEndpoint.replace(':tenantId', tenantId)}`
        const response = await axios.post(url, data, {
          headers: {
            Authorization: `Bearer ${rootState.token}`,
            ...(isFormData ? {} : { 'Content-Type': 'application/json' })
          }
        })
        return response.data
      } catch (error) {
        commit('setError', error.response?.data?.message || `Failed to create ${resourceName}`)
        throw error
      } finally {
        commit('setLoading', false)
      }
    },

    async updateData({ commit, rootState }, { tenantId, id, data }) {
      commit('setLoading', true)
      commit('setError', null)
      try {
        const url = `${apiEndpoint.replace(':tenantId', tenantId)}/${id}`
        
        // Handle different resource types
        let updateData
        if (resourceName === 'updates') {
          updateData = {
            title: data.title,
            content: data.content,
            category: data.category,
            public_id: data.public_id
          }
        } else {
          // For other resources, send the data as is
          updateData = data
        }

        const response = await axios.put(url, updateData, {
          headers: { Authorization: `Bearer ${rootState.token}` }
        })

        return response.data
      } catch (error) {
        commit('setError', error.response?.data?.message || `Failed to update ${resourceName}`)
        throw error
      } finally {
        commit('setLoading', false)
      }
    },

    async deleteData({ commit, rootState }, { tenantId, id }) {
      commit('setLoading', true)
      commit('setError', null)
      try {
        const url = `${apiEndpoint.replace(':tenantId', tenantId)}/${id}`
        await axios.delete(url, {
          headers: { Authorization: `Bearer ${rootState.token}` }
        })
      } catch (error) {
        commit('setError', error.response?.data?.message || `Failed to delete ${resourceName}`)
        throw error
      } finally {
        commit('setLoading', false)
      }
    },

    async mapCategoryToDocument({ commit, rootState }, { tenantId, categoryId, documentId }) {
      if (resourceName !== 'securityCategories') return
      commit('setLoading', true)
      commit('setError', null)
      try {
        const url = `${apiEndpoint.replace(':tenantId', tenantId)}/${categoryId}/documents/${documentId}`
        const response = await axios.post(url, {}, {
          headers: { Authorization: `Bearer ${rootState.token}` }
        })
        return response.data
      } catch (error) {
        commit('setError', error.response?.data?.message || 'Failed to map category to document')
        throw error
      } finally {
        commit('setLoading', false)
      }
    },

    async unmapCategoryFromDocument({ commit, rootState }, { tenantId, categoryId, documentId }) {
      if (resourceName !== 'securityCategories') return
      commit('setLoading', true)
      commit('setError', null)
      try {
        const url = `${apiEndpoint.replace(':tenantId', tenantId)}/${categoryId}/documents/${documentId}`
        await axios.delete(url, {
          headers: { Authorization: `Bearer ${rootState.token}` }
        })
      } catch (error) {
        commit('setError', error.response?.data?.message || 'Failed to unmap category from document')
        throw error
      } finally {
        commit('setLoading', false)
      }
    }
  }
})

export default createDataModule